import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Check, X, Trash2 } from "lucide-react";
import { useSEO } from "@/lib/useSEO";
import { decideRobots, buildPageMeta } from "@/lib/seoIndex";

export default function AdminPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  useSEO({ robots: decideRobots({ pageType: "account" }), ...buildPageMeta("account") });
  const nav = useNavigate();
  const [tab, setTab] = useState("pending");
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  /* eslint-disable */
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/offers", { params: tab === "all" ? {} : { status: tab } });
      setOffers(data);
    } finally { setLoading(false); }
  }, [tab]);

  useEffect(() => {
    if (user === false) nav("/login?redirect=/admin", { replace: true });
    if (user && user.role !== "admin") nav("/", { replace: true });
  }, [user, nav]);

  useEffect(() => {
    if (user && user.role === "admin") load();
  }, [user, load]);

  const act = async (id, action) => {
    if (action === "delete") {
      if (!window.confirm(t("admin.confirm_delete"))) return;
      await api.delete(`/admin/offers/${id}`);
    } else {
      await api.post(`/admin/offers/${id}/${action}`);
    }
    await load();
  };
  /* eslint-enable */

  if (!user || user.role !== "admin") return null;

  const TAB_LABEL = {
    pending: t("admin.tab_pending"),
    approved: t("admin.tab_approved"),
    rejected: t("admin.tab_rejected"),
    all: t("admin.tab_all"),
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-12" data-testid="admin-page">
      <p className="eyebrow">{t("admin.eyebrow")}</p>
      <h1 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">{t("admin.title")}</h1>

      <div className="mt-8 inline-flex rounded-full border border-[#DED7C7] bg-white p-1" data-testid="admin-tabs">
        {["pending", "approved", "rejected", "all"].map(tk => (
          <button
            key={tk}
            onClick={() => setTab(tk)}
            className={`btn-pill px-4 py-2 text-sm font-semibold transition ${tab === tk ? "bg-[#5B8C66] text-white" : "text-[#1F2A24]"}`}
            data-testid={`admin-tab-${tk}`}
          >{TAB_LABEL[tk]}</button>
        ))}
      </div>

      <div className="mt-8 card-clay overflow-hidden">
        {loading ? <div className="p-8 text-[#5F6B61]">{t("admin.loading")}</div> :
          offers.length === 0 ? <div className="p-8 text-[#5F6B61]">{t("admin.empty")}</div> :
          (
            <div className="divide-y divide-[#DED7C7]">
              {offers.map(o => (
                <div key={o.id} className="p-5 sm:p-6 flex items-start gap-4 flex-wrap" data-testid={`admin-row-${o.id}`}>
                  <div className="flex-1 min-w-[260px]">
                    <p className="text-xs eyebrow">{t(`countries.${o.country}`, { defaultValue: o.country })} · {t(`categories.${o.category}`, { defaultValue: o.category.replace("_"," ") })}</p>
                    <h3 className="mt-1 font-bold">{o.title}</h3>
                    <p className="text-sm text-[#5F6B61]">{o.organization} — {t(`causes.${o.cause_area}`, { defaultValue: o.cause_area })}</p>
                    <p className="mt-2 text-sm line-clamp-2 text-[#1F2A24]">{o.description}</p>
                    <span className={`mt-2 inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${
                      o.status === "approved" ? "bg-[#E0EBD3] text-[#3F6147]" :
                      o.status === "pending" ? "bg-[#F2E9D5] text-[#7C642A]" :
                      "bg-[#F5DDD0] text-[#8E4A2E]"
                    }`}>{o.status}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {o.status !== "approved" && (
                      <button onClick={() => act(o.id, "approve")} className="btn-pill px-3 py-1.5 text-xs font-bold bg-[#5B8C66] text-white hover:opacity-90 inline-flex items-center gap-1" data-testid={`admin-approve-${o.id}`}>
                        <Check size={12}/> {t("admin.approve")}
                      </button>
                    )}
                    {o.status !== "rejected" && (
                      <button onClick={() => act(o.id, "reject")} className="btn-pill px-3 py-1.5 text-xs font-bold border border-[#DED7C7] hover:border-[#3F6147] hover:text-[#3F6147] inline-flex items-center gap-1" data-testid={`admin-reject-${o.id}`}>
                        <X size={12}/> {t("admin.reject")}
                      </button>
                    )}
                    <button onClick={() => act(o.id, "delete")} className="btn-pill px-3 py-1.5 text-xs font-bold text-[#3F6147] hover:bg-[#E9F0E0] inline-flex items-center gap-1" data-testid={`admin-delete-${o.id}`}>
                      <Trash2 size={12}/>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )
        }
      </div>
    </div>
  );
}
